import {
	Accordion,
	Button,
	Header,
	Label,
	List,
	Segment,
	Table,
} from '@mairistem/composants';
import { Icon } from '@mairistem/finances-utils';
import React from 'react';
import type { AnomalieImportPes } from '../../services/pes/importPes';
import { plagesLignes } from '../../utils/pes/compteRendu';
import { pluriel } from '../../utils/pes/format';

/** Au-delà, la liste est repliée derrière « Afficher » : une cause peut citer des centaines de pièces. */
const NOMBRE_VISIBLE = 15;

/** Bouton qui montre le reste d'une liste repliée, ou la replie. */
const BoutonSuite = ({
	total,
	ouvert,
	onClick,
	quoi,
}: {
	total: number;
	ouvert: boolean;
	onClick: () => void;
	quoi: string;
}) =>
	total > NOMBRE_VISIBLE ? (
		<Button basic compact size="tiny" type="button" onClick={onClick}>
			{ouvert
				? `Réduire la liste des ${quoi}`
				: `Afficher les ${total - NOMBRE_VISIBLE} autres ${quoi}`}
		</Button>
	) : null;

interface GroupeAnomalieProps {
	anomalie: AnomalieImportPes;
	identifiant: string;
	ouvert: boolean;
	onBasculer: () => void;
	/** colonne Fichier : seulement si l'import en compte plusieurs (zip) */
	avecFichier?: boolean;
}

/**
 * Une cause d'anomalie du compte rendu, en pli d'accordéon : titre et contenu sont enfants directs de l'Accordion
 * (le thème ne replie que ceux-là). Le titre, focalisable, se bascule au clic, à Entrée et à Espace ; il dit la cause
 * et combien de pièces et d'éléments elle touche ; le contenu liste les éléments concernés (tiers, imputations…) et
 * les pièces à corriger dans le fichier.
 */
const GroupeAnomalie = ({
	anomalie,
	identifiant,
	ouvert,
	onBasculer,
	avecFichier = true,
}: GroupeAnomalieProps) => {
	const [toutesPieces, setToutesPieces] = React.useState(false);
	const [tousDetails, setTousDetails] = React.useState(false);
	const pieces = toutesPieces
		? anomalie.pieces
		: anomalie.pieces.slice(0, NOMBRE_VISIBLE);
	const details = tousDetails
		? anomalie.details
		: anomalie.details.slice(0, NOMBRE_VISIBLE);
	const avecLignes = anomalie.pieces.some((piece) => piece.lignes.length > 0);
	// message sur plusieurs lignes (ex. disponible dépassé, une ligne par imputation) : la première fait le titre,
	// les suivantes sont listées dans le pli
	const [titre, ...suite] = anomalie.message
		.split('\n')
		.map((ligne) => ligne.replace(/^\s*-\s*/, '').trim());
	const precisions = suite.filter((ligne) => ligne !== '');

	return (
		<>
			<Accordion.Title
				active={ouvert}
				id={`${identifiant}-titre`}
				role="button"
				tabIndex={0}
				aria-expanded={ouvert}
				aria-controls={`${identifiant}-contenu`}
				onClick={onBasculer}
				onKeyDown={(event: React.KeyboardEvent) => {
					if (event.key === 'Enter' || event.key === ' ') {
						event.preventDefault();
						onBasculer();
					}
				}}
			>
				<Icon name="dropdown" />
				{titre}{' '}
				{anomalie.pieces.length > 0 && (
					<Label size="mini" basic>
						{pluriel(anomalie.pieces.length, 'pièce')}
					</Label>
				)}
				{anomalie.details.length > 0 && (
					<Label size="mini" basic>
						{pluriel(anomalie.details.length, 'élément')}
					</Label>
				)}
			</Accordion.Title>
			<Accordion.Content
				active={ouvert}
				id={`${identifiant}-contenu`}
				role="region"
				aria-labelledby={`${identifiant}-titre`}
			>
				<Segment basic>
					{precisions.length > 0 && <List bulleted items={precisions} />}
					{anomalie.details.length > 0 && (
						<>
							<Header as="h5">Éléments concernés</Header>
							<List bulleted items={details} />
							<BoutonSuite
								total={anomalie.details.length}
								ouvert={tousDetails}
								onClick={() => setTousDetails((actuel) => !actuel)}
								quoi="éléments"
							/>
						</>
					)}
					{anomalie.pieces.length > 0 && (
						<>
							<Header as="h5">Pièces à corriger dans le fichier</Header>
							{/* tableau gardé en colonnes sur mobile, défilant horizontalement */}
							<div style={{ overflowX: 'auto' }}>
								<Table compact celled striped unstackable size="small">
									<Table.Header>
										<Table.Row>
											{avecFichier && (
												<Table.HeaderCell scope="col">Fichier</Table.HeaderCell>
											)}
											<Table.HeaderCell scope="col" collapsing>
												Bordereau
											</Table.HeaderCell>
											<Table.HeaderCell scope="col" collapsing>
												Pièce
											</Table.HeaderCell>
											<Table.HeaderCell scope="col">Objet</Table.HeaderCell>
											{avecLignes && (
												<Table.HeaderCell scope="col" collapsing>
													Lignes
												</Table.HeaderCell>
											)}
										</Table.Row>
									</Table.Header>
									<Table.Body>
										{pieces.map((piece, rang) => (
											<Table.Row
												// biome-ignore lint/suspicious/noArrayIndexKey: liste jamais réordonnée (seule sa fin s'allonge), et deux pièces sans IdPce ont le même numéro (« - »)
												key={`${rang}|${piece.fichier}|${piece.bordereau}|${piece.piece}`}
											>
												{avecFichier && (
													<Table.Cell>{piece.fichier}</Table.Cell>
												)}
												<Table.Cell>{piece.bordereau}</Table.Cell>
												<Table.Cell>{piece.piece}</Table.Cell>
												<Table.Cell>{piece.objet ?? ''}</Table.Cell>
												{avecLignes && (
													<Table.Cell>{plagesLignes(piece.lignes)}</Table.Cell>
												)}
											</Table.Row>
										))}
									</Table.Body>
									{anomalie.pieces.length > NOMBRE_VISIBLE && (
										<Table.Footer>
											<Table.Row>
												<Table.HeaderCell
													colSpan={3 + Number(avecFichier) + Number(avecLignes)}
												>
													<BoutonSuite
														total={anomalie.pieces.length}
														ouvert={toutesPieces}
														onClick={() => setToutesPieces((actuel) => !actuel)}
														quoi="pièces"
													/>
												</Table.HeaderCell>
											</Table.Row>
										</Table.Footer>
									)}
								</Table>
							</div>
						</>
					)}
					<Header as="h6" disabled>
						Code {anomalie.code}
					</Header>
				</Segment>
			</Accordion.Content>
		</>
	);
};

export default GroupeAnomalie;
